import { useEffect, useState, type JSX } from 'react'
import { request, stampWithZone } from '../lib/api'
import { ErrorBox, Loading } from './DataTable'
import { Status, Toggletip, type Tone } from './kit'

// What the vendor did with one questionnaire (release 3.3, item 35): the
// invitation, each reminder, each visit to the portal (a code sent or refused,
// signing in, the first answer) and the acts on the record (submitted, files,
// questions, scored), newest first, from GET /v1/assessments/{id}/timeline.
//
// It answers the question a reviewer asks before chasing anybody: did the
// vendor get it, did they get in, have they started. It names nobody: the
// invited contact is on the vendor record, behind its own permission.

export type TimelineEvent = {
  at: string
  source: 'invitation' | 'reminder' | 'portal' | 'record'
  code: string
  label: string
  detail?: string
  // unverified: somebody at the link who has not signed in (a refused code,
  // a slow-down). Not the vendor being active.
  who: 'vendor' | 'unverified' | 'team' | 'system'
  tone: 'good' | 'warn' | 'info'
}

export type Timeline = {
  events: TimelineEvent[]
  lastVendorAt: string
  truncated: boolean
}

export function timelineFrom(raw: Record<string, unknown>): Timeline {
  const events = Array.isArray(raw.events) ? (raw.events as TimelineEvent[]) : []
  return {
    events,
    lastVendorAt: typeof raw.lastVendorAt === 'string' ? raw.lastVendorAt : '',
    truncated: raw.truncated === true,
  }
}

const WHO: Record<TimelineEvent['who'], string> = {
  vendor: 'Vendor',
  unverified: 'Not signed in',
  team: 'Your team',
  system: 'Automatic',
}

const TONE: Record<TimelineEvent['tone'], { tone: Tone; word: string } | null> = {
  good: { tone: 'ok', word: 'Done' },
  warn: { tone: 'warn', word: 'Check' },
  info: null,
}

// The one line above the table: the last time the vendor did anything, or
// that they have not been seen.
export function lastSeenLine(t: Timeline): string {
  if (!t.lastVendorAt) {
    return t.events.some((e) => e.source === 'invitation' || e.source === 'reminder')
      ? 'The vendor has not signed in yet'
      : 'Nothing has been sent to the vendor yet'
  }
  return 'Vendor last active ' + stampWithZone(t.lastVendorAt)
}

export function AssessmentActivity({
  assessmentId,
  onLoaded,
}: {
  assessmentId: string
  onLoaded?: (t: Timeline) => void
}): JSX.Element {
  const [timeline, setTimeline] = useState<Timeline | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let live = true
    setTimeline(null)
    setError(null)
    request<Record<string, unknown>>('/v1/assessments/' + encodeURIComponent(assessmentId) + '/timeline')
      .then((raw) => {
        if (!live) return
        const t = timelineFrom(raw)
        setTimeline(t)
        onLoaded?.(t)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
    // onLoaded is a parent's setter; reading again on every render of the
    // parent would be a request per keystroke on the answers tab.
  }, [assessmentId])

  return (
    <section className="panel panel-flush" aria-labelledby="assessment-activity-heading">
      <div className="panel-head">
        <h2 id="assessment-activity-heading" style={{ margin: 0 }}>
          What the vendor did
        </h2>
        <p className="summary-line" style={{ margin: 0 }}>
          {timeline === null ? <span className="muted">not read yet</span> : lastSeenLine(timeline)}{' '}
          <Toggletip label="About this list">
            Newest first: the invitation, each reminder, each visit to the vendor portal and what was done on the
            record. A code is only sent to an address that was invited, works once, expires and works only in the
            browser that asked for it. No address, code or phone number is shown here; the invited contact is on the
            vendor record.
          </Toggletip>
        </p>
      </div>

      {error != null ? (
        <div style={{ padding: '0 16px 12px' }}>
          <ErrorBox error={error} what="What the vendor did" />
        </div>
      ) : timeline === null ? (
        <div style={{ padding: '0 16px 12px' }}>
          <Loading what="the vendor's activity" />
        </div>
      ) : (
        <ActivityList timeline={timeline} />
      )}
      {timeline?.truncated && (
        <p className="muted" style={{ margin: '8px 16px 12px' }}>
          Showing the newest 200. The full record is on the Audit page.
        </p>
      )}
    </section>
  )
}

// The list itself, apart from the fetch, so a test can draw it from a reply.
export function ActivityList({ timeline }: { timeline: Timeline }): JSX.Element {
  if (timeline.events.length === 0) {
    return <div className="empty">Nothing yet. The list fills in once the questionnaire is sent.</div>
  }
  return (
    <table className="ev-table act-table">
      <caption className="vh">What the vendor did, newest first</caption>
      <thead>
        <tr>
          <th scope="col">When</th>
          <th scope="col">What happened</th>
          <th scope="col">By</th>
        </tr>
      </thead>
      <tbody>
        {timeline.events.map((e, i) => {
          const mark = TONE[e.tone] ?? null
          return (
            <tr key={e.at + e.code + i}>
              <td className="act-when">{stampWithZone(e.at)}</td>
              <td>
                {mark && <Status tone={mark.tone}>{mark.word}</Status>} {e.label}
                {e.detail && <div className="muted">{e.detail}</div>}
              </td>
              <td>{WHO[e.who] ?? e.who}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
